import React, {useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Tabs,
  Tab,
  Stack,
  Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Answered Icon
import CancelIcon from '@mui/icons-material/Cancel'; // Unanswered Icon
import {LoadingButton} from '@mui/lab';

// 🛠 Define TypeScript Interfaces
interface Question {
  id: number;
  type: number;
  answer: string[] | string | boolean | null;
}
interface QuestionAnswered {
  id: number;
  type: number;
  isanswered: boolean;
  sequence:string;
  answers:
    | string[]
    | string
    | boolean
    | {
        left: string;
        right: string;
      }[];
}
interface ReviewAttemptDialogProps {
  // eslint-disable-next-line react/boolean-prop-naming
  open: boolean;
  isPreviewLoading: boolean;
  onClose: () => void;
  questions: any[]; // Attempted questions
  questionsPassed: QuestionAnswered[]; // All questions
  handleSave: () => void;
}

const ReviewAttemptDialog: React.FC<ReviewAttemptDialogProps> = ({
  open,
  onClose,
  isPreviewLoading,
  questions,
  questionsPassed,
  handleSave
}) => {
  const [tabValue, setTabValue] = useState<number>(0); // 0 = All Questions, 1 = Unanswered
  // 🛠 Merge questionsPassed with questions, ensuring unattempted questions are included
  // Ensure `questions` is an array
  const mergedQuestions: QuestionAnswered[] = questionsPassed?.map(
    question => ({
      ...question,
      answers: questions.find(q => q.id === question.id)?.answers ?? '' // Use the existing answer if available; otherwise, null
    })
  );

  // const answeredQuestions = mergedQuestions?.filter(q => q.answers);
  const unansweredQuestions = mergedQuestions?.filter(
    q =>
      !q.answers ||
      (Array.isArray(q.answers) && q.answers.length === 0) ||
      (typeof q.answers === 'string' && q.answers.trim() === '')
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{fontWeight: 'bold'}}>Review attempt</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{mb: 2}}>
          Review the questions you have answered and skipped
        </Typography>

        {/* Tabs for "All Questions" and "Unanswered Questions" */}
        <Tabs
          value={tabValue}
          onChange={(event, newValue) => setTabValue(newValue)}
          centered>
          <Tab
            label={`All Questions ${mergedQuestions?.length - unansweredQuestions?.length}`}
          />
          <Tab
            label={`Unanswered Questions ${unansweredQuestions?.length}`}
            sx={{color: 'red'}}
          />
        </Tabs>

        {/* Display questions based on selected tab */}
        <Stack spacing={1.5} sx={{mt: 2}}>
          {(tabValue === 0 ? mergedQuestions : unansweredQuestions).map(
            (question, index) => {
              const isAnswered = questions?.some(
                q => q.id === question.id && q.isanswered
              );
              return (
                <Paper
                  key={question.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 1.5,
                    borderRadius: '8px',
                    backgroundColor: isAnswered ? '#e8f5e9' : '#ffebee',
                    border: `1px solid ${isAnswered ? '#66bb6a' : '#f44336'}`
                  }}>
                  {isAnswered ? (
                    <CheckCircleIcon sx={{color: '#66bb6a', mr: 1}} />
                  ) : (
                    <CancelIcon sx={{color: '#f44336', mr: 1}} />
                  )}
                  <Typography variant="body1">{`Question ${question.sequence}`}</Typography>
                </Paper>
              );
            }
          )}
        </Stack>
      </DialogContent>

      {/* Buttons */}
      <DialogActions sx={{justifyContent: 'space-between', px: 3, pb: 2}}>
        <LoadingButton
          loading={isPreviewLoading}
          variant="contained"
          color="primary"
          onClick={handleSave}>
          Complete Test
        </LoadingButton>
        <LoadingButton
          disabled={isPreviewLoading}
          variant="outlined"
          onClick={onClose}>
          Return to Test
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewAttemptDialog;
